import {
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
  type EnvironmentProviders,
} from '@angular/core';
import type { EnvironmentConfig } from './environment.service';
import { ENVIRONMENT_CONFIG, EnvironmentService } from './environment.service';

/**
 * Registers environment configuration for the current application build.
 *
 * Provides the config both as a synchronous injection token (available at DI
 * construction time) and via an `APP_INITIALIZER` that calls {@link EnvironmentService.setConfig}
 * so the service reflects the correct values before any bootstrapped code runs.
 *
 * Always place this before `provideAnalytics()` and any other provider that may
 * trigger `EnvironmentService` injection.
 *
 * @example
 * ```typescript
 * // In app.config.ts
 * export const getAppConfig = (): ApplicationConfig => ({
 *   providers: [
 *     provideEnvironment(environment),
 *     provideAnalytics({ ... }),
 *   ],
 * });
 * ```
 */
export function provideEnvironment(
  config: Partial<EnvironmentConfig>
): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: ENVIRONMENT_CONFIG, useValue: config },
    provideAppInitializer(() => {
      inject(EnvironmentService).setConfig(config);
    }),
  ]);
}
